import { createSlice } from '@reduxjs/toolkit';
// utils
import { selectFrom, selectOne, callRPC, insertInto, updateTable } from '../../services/databaseService';
import { invokeFunction, sendWhatsappTabLink, notifyTabOpened } from '../../services/edgeFunctionService';
import { supabase } from '../../services/supabase';
import { Client, CreateNewTab, CustomerOpenTab, KanbanColumn, Tab, TabState } from '../../@types/tab';
// _mock
import { activeTabs, allKanbanTabs } from '../../_mock';
// redux
import { dispatch } from '../store';
import { getCurrentTenantVenueParams } from '../../utils/tenantVenue';
import { toBrazilPhoneE164 } from '../../utils/phone';

// ----------------------------------------------------------------------

function objFromArray<Type extends Record<string, any>>(array: Type[], key = 'id') {
  return array.reduce<Record<string, Type>>((accumulator, current) => {
    accumulator[current[key]] = current;
    return accumulator;
  }, {});
}

const initialState: TabState = {
  isLoading: false,
  error: null,
  tabs: [],
  tab: null,
  customer: null,
  customerOpenTab: null,
  openTabs: [],
  isOpenModal: false,
  isOpenCodeModal: false,
  isOpenNotifyModal: false,
  sortBy: null,
  filterInactiveTabs: false,
  customerTab: {
    activeStep: 0,
    accessToken: null,
    paymentSecret: null,
    paymentId: null,
    pixCharge: null,
    tab: null,
    emailSent: null as boolean | null,
    checkoutMode: null,
  },
  newTab: {
    activeStep: 0,
    customer: null,
    venue: null,
    tableId: null,
  },
  board: {
    cards: {},
    columns: {},
    columnOrder: [],
  },
  statistics: {
    tabsSummary: {
      label: '',
      value: 0,
      count: 0,
      percent: 0,
      total: 0,
      average: 0,
      data: [],
    },
    publicSummary: {
      label: '',
      value: 0,
      count: 0,
      percent: 0,
      total: 0,
      average: 0,
      data: [],
    },
    tabMonthlyTotals: [],
    publicMonthlyTotals: [],
    totalsByProduct: [],
    totalsByStatus: [],
    totalsByType: [],
    totalsByVenue: [],
    totalsByTypePerWeekday: [],
  },
  filters: {
    client: [],
    category: 'All',
    priceRange: [0, 200],
    rating: '',
    status: [],
  }
};

const slice = createSlice({
  name: 'tab',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },

     // GET STATISTICS
    getTabsStatisticsSuccess(state, action) {
      state.statistics = action.payload;
      state.isLoading = false;
    },

    // GET TABS
    getTabsSuccess(state, action) {
      const tabs = Array.isArray(action.payload) ? action.payload : action.payload.tabs || [];

      const filterInactiveTabs = state.filterInactiveTabs;

      const tabsData = filterInactiveTabs ? tabs : tabs.filter((tab: { tabstatus: number; }) => tab.tabstatus > 0 && tab.tabstatus < 4)

      const cards = objFromArray<Tab>(tabsData);

      const columnOrder = filterInactiveTabs ? allKanbanTabs : activeTabs;

      // Group tabs by tabstatus
      const groupedTabs = tabsData.reduce((acc: { [x: string]: any[]; }, tab: { tabstatus: any; id: any; }) => {
        const { tabstatus, id } = tab;
        if (!acc[tabstatus]) {
            acc[tabstatus] = [];
        }
        acc[tabstatus].push(id);
        return acc;
      }, {});

      const columnsWithData = Object.keys(groupedTabs).reduce((acc, tabstatus) => {
        acc[tabstatus] = {
          id: tabstatus,
          name: tabstatus,
          cardIds: groupedTabs[tabstatus] || []
        };
        return acc;
      }, {} as Record<string, KanbanColumn>);

      const emptyColumns = columnOrder.filter((columnId) => !columnsWithData[columnId]);
      const emptyColumnsData = emptyColumns.reduce((acc, columnId) => {
        acc[columnId] = {
          id: columnId,
          name: columnId,
          cardIds: []
        };
        return acc;
      }, {} as Record<string, KanbanColumn>);

      // Merge empty columns data into columns
      const mergedColumns = { ...columnsWithData, ...emptyColumnsData };

      // Sort columns by id in ascending tab
      const sortedColumns = Object.keys(mergedColumns)
        .sort((a, b) => a.localeCompare(b))
        .reduce((acc, key) => {
          acc[key] = mergedColumns[key];
          return acc;
        }, {} as Record<string, KanbanColumn>);

      const columns = sortedColumns;

      state.tabs = tabs;
      state.board = {
        cards,
        columns,
        columnOrder,
      };
      state.isLoading = false;
    },

    // GET TAB
    getTabSuccess(state, action) {
      state.tab = action.payload;
      state.isLoading = false;
    },

    // GET CUSTOMER TAB
    getCustomerTabSuccess(state, action) {
      state.customerTab.tab = action.payload.tab;
      state.customerTab.accessToken = action.payload.accessToken;
      state.customerTab.activeStep = 1;

      state.isLoading = false;
    },

    // PATCH CUSTOMER TAB TIP (Fastpass, before PI)
    setCustomerTabTipSuccess(state, action) {
      if (state.customerTab.tab) {
        state.customerTab.tab = {
          ...state.customerTab.tab,
          serviceTip: action.payload.tip,
        };
        // Clear stale payment secrets so card/PIX recreate with new amount
        state.customerTab.paymentSecret = null;
        state.customerTab.paymentId = null;
        state.customerTab.pixCharge = null;
      }
    },

    // GET UPDATED CUSTOMER TAB
    getUpdatedCustomerTabSuccess(state, action) {
      state.customerTab.tab = action.payload;
      state.customerTab.activeStep = 3;

      state.isLoading = false;
    },

    // GET CUSTOMER
    getCustomerSuccess(state, action) {
      const payload = action.payload as {
        customer: TabState['customer'];
        openTab?: CustomerOpenTab;
      };
      // Back-compat: payload may still be a bare Client.
      if (payload && typeof payload === 'object' && 'customer' in payload) {
        state.customer = payload.customer;
        state.customerOpenTab = payload.openTab ?? null;
      } else {
        state.customer = action.payload;
        state.customerOpenTab = null;
      }
      state.isLoading = false;
    },

    setCustomerOpenTab(state, action) {
      state.customerOpenTab = action.payload;
    },

    // GET PAYMENT SECRET
    getPaymentTokensSuccess(state, action) {
      const tokens = action.payload;
      state.customerTab.paymentSecret = tokens.clientSecret;
      state.customerTab.paymentId = tokens.paymentIntentId;

      state.isLoading = false;
    },

    // GET PIX CHARGE
    getPixChargeSuccess(state, action) {
      state.customerTab.pixCharge = action.payload;
      state.isLoading = false;
    },

    // CONFIRMATION EMAIL STATUS
    setEmailSentStatus(state, action) {
      state.customerTab.emailSent = action.payload;
    },

    resetCustomer(state) {
      state.customer = null;
      state.customerOpenTab = null;
    },

    resetCustomerTab(state) {
      state.customerTab.activeStep = 0;
      state.customerTab.accessToken = null;
      state.customerTab.paymentSecret = null;
      state.customerTab.pixCharge = null;
      state.customerTab.tab = null;
      state.customerTab.emailSent = null;
      state.customerTab.checkoutMode = null;
      state.isLoading = false;
    },

    setCustomerTabCheckoutMode(state, action) {
      state.customerTab.checkoutMode = action.payload;
    },

    // CREATE TAB
    createTabSuccess(state, action) {
      state.openTabs = action.payload;
      state.isLoading = false;
    },

    //  SORT & FILTER TABS
    sortByTabs(state, action) {
      state.sortBy = action.payload;
    },

    filterInactives(state) {
      const filterInactiveTabs = !state.filterInactiveTabs;
      const tabs = state.tabs;

      const tabsData = filterInactiveTabs ? tabs : tabs.filter(tab => tab.tabstatus > 0 && tab.tabstatus <= 3)

      const cards = objFromArray<Tab>(tabsData);

      const columnOrder = filterInactiveTabs ? allKanbanTabs : activeTabs;

      // Group tabs by tabstatus
      const groupedTabs = tabsData.reduce((acc: { [x: string]: any[]; }, tab: { tabstatus: any; id: any; }) => {
        const { tabstatus, id } = tab;
        if (!acc[tabstatus]) {
            acc[tabstatus] = [];
        }
        acc[tabstatus].push(id);
        return acc;
      }, {});

      const columnsWithData = Object.keys(groupedTabs).reduce((acc, tabstatus) => {
        acc[tabstatus] = {
          id: tabstatus,
          name: tabstatus,
          cardIds: groupedTabs[tabstatus] || []
        };
        return acc;
      }, {} as Record<string, KanbanColumn>);

      const emptyColumns = columnOrder.filter((columnId) => !columnsWithData[columnId]);
      const emptyColumnsData = emptyColumns.reduce((acc, columnId) => {
        acc[columnId] = {
          id: columnId,
          name: columnId,
          cardIds: []
        };
        return acc;
      }, {} as Record<string, KanbanColumn>);

      // Merge empty columns data into columns
      const mergedColumns = { ...columnsWithData, ...emptyColumnsData };

      // Sort columns by id in ascending tab
      const sortedColumns = Object.keys(mergedColumns)
        .sort((a, b) => a.localeCompare(b))
        .reduce((acc, key) => {
          acc[key] = mergedColumns[key];
          return acc;
        }, {} as Record<string, KanbanColumn>);

      const columns = sortedColumns;

      state.filterInactiveTabs = filterInactiveTabs;
      state.board = {
        cards,
        columns,
        columnOrder,
      };
    },

    filterTabs(state, action) {
      state.filters.client = action.payload.client;
      state.filters.category = action.payload.category;
      state.filters.priceRange = action.payload.priceRange;
      state.filters.rating = action.payload.rating;
    },

    // OPEN MODAL
    openModal(state) {
      state.isOpenModal = true;
    },

    // OPEN CODE MODAL
    openCodeModal(state) {
      state.isOpenCodeModal = true;
    },

    // OPEN NOTIFY MODAL
    openNotifyModal(state) {
      state.isOpenNotifyModal = true;
    },

    // CLOSE MODAL
    closeModal(state) {
      state.isOpenModal = false;
      state.isOpenCodeModal = false;
      state.isOpenNotifyModal = false;
    },

    // NEW TAB
    getNewTab(state, action) {
      const newTab = action.payload;

      const customer = newTab.length === 0 ? null : state.newTab.customer;
      const tableId = newTab.length === 0 ? null : state.newTab.tableId;
      const venue = newTab.length === 0 ? null : state.newTab.venue;

      state.newTab.customer = customer;
      state.newTab.tableId = tableId;
      state.newTab.venue = venue;
    },

    setNewTabVenue(state, action) {
      state.newTab.venue = action.payload;
    },

    setNewTabCustomer(state, action) {
      state.newTab.customer = action.payload;
    },

    setNewTabTable(state, action) {
      state.newTab.tableId = action.payload;
      console.log('setNewTabTable', action.payload);
      console.log('state.newTab.tableId', state.newTab.tableId);
    },

    resetNewTab(state) {
      state.newTab.activeStep = 0;
      state.newTab.customer = null;
      state.newTab.venue = null;
      state.newTab.tableId = null;
      state.customer = null;
      state.customerOpenTab = null;
    },

    onBackStep(state, action) {
      const mode = action.payload

      if (mode === 'fastpass') {
        // Reset the tab if going back to the first step
        if (state.customerTab.activeStep === 1) {
          state.customerTab.tab = null;
          state.customerTab.activeStep = 0;
          state.customerTab.paymentSecret = null;

        } else {
          state.customerTab.activeStep -= 1;

        }
        
      } else if (mode === 'new') {
        state.newTab.activeStep -= 1;

      }
    },

    onNextStep(state, action) {
      const mode = action.payload

      if (mode === 'fastpass') {
        state.customerTab.activeStep += 1;

      } else if (mode === 'new') {
        state.newTab.activeStep += 1;

      }
    },

    onGotoStep(state, action) {
      const { mode, step } = action.payload;
      
      if (mode === 'fastpass') {
        state.customerTab.activeStep = step;

      } else if (mode === 'new') {
        state.newTab.activeStep = step;

      }
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  resetCustomer,
  resetCustomerTab,
  getNewTab,
  setNewTabCustomer,
  setNewTabVenue,
  setNewTabTable,
  resetNewTab,
  onGotoStep,
  onBackStep,
  onNextStep,
  filterTabs,
  filterInactives,
  openModal,
  closeModal,
  openCodeModal,
  openNotifyModal,
  sortByTabs,
  startLoading,
  stopLoading,
  hasError,
  setCustomerTabCheckoutMode,
} = slice.actions;

// ----------------------------------------------------------------------

function parseBirthDateForGuest(value?: string | Date | number | null): string | null {
  if (value == null || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  const raw = String(value).trim();
  // dd/MM/yyyy from tab create form
  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  // already ISO date
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return null;
}

export function createNewTab(data: {
  user_id?: string;
  customer?: Client | null;
  venue_id: string;
  venue_table_id?: number;
  venue_user_id?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      let userId = data.user_id?.trim() || '';

      // New CPF (no existing user id): create/find guest before opening the tab
      if (!userId) {
        const customer = data.customer;
        if (!customer?.govId || !customer.firstName || !customer.lastName) {
          throw new Error('Customer details are required to create a guest tab.');
        }

        const guest = await callRPC<{
          id: string;
          firstName?: string;
          lastName?: string;
          govId?: string;
          phoneNumber?: string;
          email?: string;
        }>('ensure_guest_user', {
          p_firstname: customer.firstName,
          p_lastname: customer.lastName,
          p_govid: customer.govId,
          p_phonenumber: toBrazilPhoneE164(customer.phoneNumber),
          p_birthdate: parseBirthDateForGuest(customer.birthDate),
          p_gender: customer.gender || 'other',
        });

        userId = guest?.id || '';
        if (!userId) {
          throw new Error('Failed to create guest user.');
        }

        dispatch(
          slice.actions.setNewTabCustomer({
            ...customer,
            id: userId,
            email: guest.email || customer.email,
            phoneNumber: guest.phoneNumber || customer.phoneNumber,
          }),
        );
      }

      // Use RPC function to create tab
      // create_tab is RETURNS TABLE — Supabase returns an array; take first row
      const resultRaw = await callRPC<any>('create_tab', {
        p_user_id: userId,
        p_venue_id: data.venue_id,
        p_venue_table_id: data.venue_table_id || null,
        p_venue_user_id: data.venue_user_id || null,
      });
      const result = Array.isArray(resultRaw) ? resultRaw[0] : resultRaw as {
        tab_id: number;
        tabcode: number;
        total: number;
        subtotal: number;
        tabstatus: number;
      };

      // Fetch the created tab
      const tab = await selectOne<any>('tabs', { id: result.tab_id }, `
        *,
        users!tabs_user_id_fkey (
          id,
          firstname,
          lastname,
          photo_url,
          email
        ),
        venues!tabs_venue_id_fkey (
          id,
          name,
          photo_urls,
          openstatus
        )
      `);

      if (tab) {
        const transformedTab: Tab = {
          id: tab.id.toString(),
          tabstatus: tab.tabstatus || 0,
          paymentStatus: tab.paymentStatus ?? tab.paymentstatus ?? 1,
          sent: tab.sent || 0,
          tabNumber: tab.tabnumber?.toString() || tab.tabcode?.toString() || '',
          total: parseFloat(tab.total || 0),
          subtotal: parseFloat(tab.subtotal || 0),
          serviceTip: parseFloat(tab.service_tip || 0),
          serviceFee:      parseFloat(tab.service_fee || 0),
          taxes:           tab.taxes || 0,
          discount:        tab.discount || 0,
          discountValue:   parseFloat(tab.discount_value || 0),
          paymentId:       tab.payment_id      ?? null,
          payment_gateway: tab.payment_gateway ?? null,
          payment_format:  tab.payment_format  ?? null,
          gateway_ref_id:  tab.gateway_ref_id  ?? null,
          tabcode: tab.tabcode?.toString() || '',
          customer: tab.users ? {
            id: tab.users.id,
            firstName: tab.users.firstname,
            lastName: tab.users.lastname,
            email: tab.users.email,
            photo_url: tab.users.photo_url,
            gender: tab.users.gender || '',
            birthDate: tab.users.birthdate || '',
            phoneNumber: tab.users.phonenumber || '',
            govId: tab.users.govid || '',
          } : {
            id: '',
            firstName: '',
            lastName: '',
            email: '',
            gender: '',
            birthDate: '',
            phoneNumber: '',
            govId: '',
          },
          venue: tab.venues || tab.venue,
          created_at: tab.created_at,
          updated_at: tab.updated_at || tab.created_at,
          created_by: tab.venue_user_createdby || '',
          updated_by: tab.venue_user_updatedby || '',
          orders_count: [],
          items_count: [],
          orders: [],
          items: [],
          logs: [],
          qrCode: tab.qrcode || '',
        };
        dispatch(slice.actions.getTabSuccess(transformedTab));
      }

      if (result?.tab_id) {
        const tabIdStr = result.tab_id.toString();

        sendWhatsappTabLink({
          user_id: userId,
          tab_id: tabIdStr,
        }).catch((err) => console.error('send-whatsapp error:', err));

        notifyTabOpened({
          user_id: userId,
          tab_id: tabIdStr,
        }).catch((err) => console.error('notify-tab-opened error:', err));
      }

      return result;
    } catch (error: any) {
      dispatch(slice.actions.hasError(error));
      console.error('Error creating tab:', error);
      throw new Error(error.message);
    }
  };
}

// ----------------------------------------------------------------------

export function assignTabTable(tabId: number, venueTableId: number) {
  return async () => {
    const result = await callRPC<{
      tab_id: number;
      table_id: number;
      name: string;
      label: string | null;
      open_count: number;
    }>('assign_tab_table', {
      p_tab_id: tabId,
      p_venue_table_id: venueTableId,
    });
    return result;
  };
}

/** Clear table assignment — only allowed when venue service_mode = hybrid. */
export function unassignTabTable(tabId: number) {
  return async () => {
    const result = await callRPC<{
      tab_id: number;
      table_id: null;
      open_count: number;
    }>('unassign_tab_table', {
      p_tab_id: tabId,
    });
    return result;
  };
}

  // ----------------------------------------------------------------------

  // Helper function to get venue_user_id from current auth user
  async function getCurrentVenueUserId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: venueUser } = await supabase
        .from('venue_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      return venueUser?.id || null;
    } catch (error) {
      console.error('Error getting venue_user_id:', error);
      return null;
    }
  }

export function getTabs(filters?: { tabstatus?: number[] }) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get current venue_user_id
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        dispatch(slice.actions.stopLoading());
        return;
      }

      // Get tenant venue parameters (organization_id or venue_id)
      const { organization_id, venue_id } = getCurrentTenantVenueParams();

      // Use RPC wrapper function with organization/venue filtering
      const tabs = await callRPC<any[]>('get_venue_tabs_filtered', {
        p_venue_user_id: venueUserId,
        p_tabstatus: filters?.tabstatus || null,
        p_organization_id: organization_id,
        p_venue_id: venue_id,
      });

      console.log('Fetched tabs:', tabs);

      // Dispatch array directly (reducer handles both array and { tabs: [...] } format)
      dispatch(slice.actions.getTabsSuccess(tabs));

    } catch (error: any) {
      dispatch(slice.actions.hasError(error));
      console.error('Error fetching tabs:', error);
    }
  };
}

// ----------------------------------------------------------------------

export function getTab(id: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Use RPC function to get tab details with joins
      const result = await callRPC<any>('get_venue_tab_details', {
        p_tab_id: parseInt(id),
      });

      if (!result || !result.tab) {
        throw new Error('Tab not found');
      }

      const tab = result.tab;

      dispatch(
        slice.actions.getTabSuccess({
          ...tab,
          id: tab.id != null ? String(tab.id) : tab.id,
        })
      );
    } catch (error: any) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

// Shared by getCustomerTab and updateCustomerTab — both receive the same
// get_customer_tab_public JSON shape (close-tab re-fetches it server-side
// after marking the tab paid, so the client can reuse this parser instead
// of relying on a direct table read, which is often blocked by RLS for
// unauthenticated fastpass guests).
function normalizeCustomerPayload(raw: any): Tab['customer'] {
  if (!raw || typeof raw !== 'object') {
    return {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      gender: '',
      birthDate: '',
      phoneNumber: '',
      govId: '',
    };
  }

  return {
    id: raw.id ?? '',
    firstName: raw.firstName ?? raw.firstname ?? '',
    lastName: raw.lastName ?? raw.lastname ?? '',
    email: raw.email ?? '',
    gender: raw.gender ?? '',
    birthDate: raw.birthDate ?? raw.birthdate ?? '',
    phoneNumber: raw.phoneNumber ?? raw.phonenumber ?? '',
    govId: raw.govId ?? raw.govid ?? '',
    photo_url: raw.photo_url,
  };
}

function normalizeTabLog(log: any) {
  if (!log || typeof log !== 'object') return log;
  return {
    ...log,
    // get_customer_tab_public uses updatedBy_*; some older payloads use updated_by_*.
    updated_by_fn: log.updated_by_fn ?? log.updatedBy_fn ?? '',
    updated_by_ln: log.updated_by_ln ?? log.updatedBy_ln ?? '',
    updated_by_name: log.updated_by_name ?? log.updatedBy_name ?? '',
  };
}

function transformCustomerTabPayload(tab: any): Tab {
  // Migration 134 renamed the owner object to `user`; keep accepting `customer`.
  const customerRaw = tab.customer ?? tab.user ?? null;

  return {
    id: tab.id.toString(),
    tabstatus: tab.tabstatus || 0,
    paymentStatus: tab.paymentStatus ?? tab.paymentstatus ?? 1,
    sent: 0,
    tabNumber: tab.tabcode?.toString() || '',
    total: parseFloat(tab.total?.toString() || '0'),
    subtotal: parseFloat(tab.subtotal?.toString() || '0'),
    serviceTip: parseFloat(tab.service_tip ?? tab.serviceTip ?? '0'),
    serviceFee: parseFloat(tab.service_fee ?? tab.serviceFee ?? tab.fee ?? '0'),
    taxes: parseFloat(tab.taxes ?? tab.service_fee ?? tab.fee ?? '0'),
    discount: tab.discount || 0,
    discountValue: parseFloat(tab.discountValue?.toString() || '0'),
    entrance_fee: parseFloat(tab.entrance_fee?.toString() || '0'),
    total_entrance_fee: parseFloat(tab.total_entrance_fee?.toString() || '0'),
    close_total: parseFloat(tab.close_total?.toString() || '0'),
    payable_total: parseFloat(tab.payable_total?.toString() || '0'),
    minimum_top_up: parseFloat(tab.minimum_top_up?.toString() || '0'),
    minimum_amount_snapshot: tab.minimum_amount_snapshot != null
      ? parseFloat(tab.minimum_amount_snapshot)
      : undefined,
    transaction_fee: parseFloat(tab.transaction_fee?.toString() || '0') || 0,
    paymentId:       tab.paymentId ?? tab.payment_id ?? null,
    payment_gateway: tab.payment_gateway  ?? null,
    payment_format:  tab.payment_format   ?? null,
    gateway_ref_id:  tab.gateway_ref_id   ?? null,
    tabcode: tab.tabcode?.toString() || '',
    customer: normalizeCustomerPayload(customerRaw),
    venue: tab.venue as any,
    created_at: tab.created_at,
    updated_at: tab.updated_at || tab.created_at,
    created_by: '',
    updated_by: '',
    orders_count: [],
    items_count: [],
    splits: tab.splits ?? [],
    orders: (tab.orders || []).map((order: any) => ({
      id: order.id.toString(),
      orderstatus: order.orderStatus ?? order.orderstatus ?? 0,
      sent: 0,
      orderNumber: order.ordercode?.toString() || order.id.toString(),
      total: parseFloat(order.total?.toString() || '0'),
      subtotal: parseFloat(order.subtotal?.toString() || '0'),
      serviceTip: parseFloat(order.tip ?? order.serviceTip ?? '0'),
      serviceFee: parseFloat(order.fee ?? order.serviceFee ?? '0'),
      taxes: 0,
      discount: 0,
      discountValue: 0,
      paymentId: order.paymentId || order.payment_id || null,
      ordercode: order.ordercode?.toString() || '',
      customer: normalizeCustomerPayload(order.customer ?? order.user),
      venue: order.venue as any,
      created_at: order.createdAt || order.created_at,
      updated_at: order.createdAt || order.created_at,
      created_by: '',
      updated_by: '',
      orders_count: [],
      items_count: [],
      orders: [],
      items: order.items || [],
      logs: [],
      qrCode: typeof order.qrCode === 'string'
        ? order.qrCode
        : order.qrCode
          ? JSON.stringify(order.qrCode)
          : '',
    })) as any,
    items: [],
    logs: (tab.logs ?? []).map(normalizeTabLog),
    qrCode: typeof tab.qrCode === 'string'
      ? tab.qrCode
      : tab.qrCode
        ? JSON.stringify(tab.qrCode)
        : '',
  };
}

// Maps known backend error codes (from get_customer_tab_public via RPC, or
// from fastpass-checkout/lookup-tab's own checks) to a stable code + message
// the UI can branch on.
function toCustomerTabError(error: any): Error {
  let errorMessage = error.message || 'Error fetching customer tab';
  let errorCode = error.code || 'UNKNOWN_ERROR';

  if (error.message) {
    if (error.message.includes('Customer not found') || error.code === 'CUSTOMER_NOT_FOUND') {
      errorCode = 'CUSTOMER_NOT_FOUND';
      errorMessage = 'Customer not found';
    } else if (error.message.includes('Tab not found') || error.code === 'TAB_NOT_FOUND') {
      errorCode = 'TAB_NOT_FOUND';
      errorMessage = 'Tab not found';
    } else if (error.message.includes('Access denied') || error.code === 'ACCESS_DENIED') {
      errorCode = 'ACCESS_DENIED';
      errorMessage = 'Access denied: This tab does not belong to this customer';
    } else if (error.code === 'RATE_LIMITED') {
      errorMessage = 'Too many attempts. Please try again later.';
    } else if (error.code === 'TURNSTILE_FAILED' || error.code === 'TURNSTILE_REQUIRED') {
      errorMessage = 'Security verification failed. Please try again.';
    } else if (error.code === 'TOKEN_EXPIRED') {
      errorMessage = 'This link has expired.';
    } else if (error.code === 'TOKEN_NOT_FOUND' || error.code === 'TOKEN_INVALID') {
      errorMessage = 'This link is invalid.';
    }
  }

  const customError = new Error(errorMessage);
  (customError as any).code = errorCode;
  (customError as any).statusCode = error.status
    ?? (errorCode === 'ACCESS_DENIED' ? 403
      : errorCode === 'TAB_NOT_FOUND' ? 404
      : errorCode === 'CUSTOMER_NOT_FOUND' ? 404
      : errorCode === 'RATE_LIMITED' ? 429
      : 500);

  return customError;
}

// Every fastpass action past the initial lookup can fail this way if the
// short-lived accessToken (30min) expires mid-checkout — components should
// send the guest back to CheckoutForm to re-enter CPF + tab number rather
// than show a raw error.
export function isSessionExpiredError(error: any): boolean {
  return ['TOKEN_EXPIRED', 'TOKEN_INVALID', 'TOKEN_REQUIRED'].includes(error?.code);
}

export function getCustomerTab(id: string, govId: string, turnstileToken: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Wraps get_customer_tab_public (not client-callable directly, see
      // backend migration 070) with captcha + rate limiting, and issues the
      // short-lived accessToken every subsequent fastpass action requires.
      const result = await invokeFunction<{ tab: any; accessToken: string }>(
        'fastpass-checkout/lookup-tab',
        {
          tabId: parseInt(id),
          govId,
          turnstileToken,
        },
      );

      if (!result?.tab) {
        throw new Error('Tab not found');
      }

      const transformedTab = transformCustomerTabPayload(result.tab);

      dispatch(slice.actions.getCustomerTabSuccess({ tab: transformedTab, accessToken: result.accessToken }));
      return transformedTab;
    } catch (error: any) {
      console.error('Error fetching customer tab:', error);

      const customError = toCustomerTabError(error);
      dispatch(slice.actions.hasError(customError));
      throw customError;
    }
  };
}

// Opens a tab directly from the shareable short link (/f/{access_token}).
// The tab access_token replaces CPF + tab number lookup on the form.
export function getCustomerTabByAccessToken(tabAccessToken: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await invokeFunction<{ tab: any; accessToken: string }>(
        'fastpass-checkout/open-by-token',
        { tabAccessToken },
      );

      if (!result?.tab) {
        throw new Error('Tab not found');
      }

      const transformedTab = transformCustomerTabPayload(result.tab);

      dispatch(slice.actions.getCustomerTabSuccess({ tab: transformedTab, accessToken: result.accessToken }));
      return transformedTab;
    } catch (error: any) {
      console.error('Error opening tab by access token:', error);

      const customError = toCustomerTabError(error);
      dispatch(slice.actions.hasError(customError));
      throw customError;
    }
  };
}

// ----------------------------------------------------------------------

export function getPaymentSecret(tab: Tab, accessToken: string | null) {
  return async () => {
    dispatch(slice.actions.startLoading());

    const { id } = tab;

    try {
      // Use the dedicated Fastpass edge function — unauthenticated guest
      // checkout, no Supabase session. Amount is recomputed server-side
      // from the tab record, never trusted from the client. accessToken
      // is the short-lived token issued by fastpass-checkout/lookup-tab,
      // proving this caller passed the CPF check for this tab.
      const result = await invokeFunction<{
        clientSecret: string;
        paymentIntentId?: string;
        paymentIntent?: string;
      }>('fastpass-payment/create-payment-intent', {
        tabId: parseInt(id),
        accessToken,
      });

      dispatch(slice.actions.getPaymentTokensSuccess({
        clientSecret: result.clientSecret,
        paymentIntentId: result.paymentIntentId ?? result.paymentIntent ?? null,
      }));
      return result;
    } catch (error: any) {
      console.error('Error getting payment secret:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

/** Persist tip on the tab before creating a Fastpass PaymentIntent / PIX charge.
 *  Pass `tipPercent` for presets so tip tracks subtotal; omit/null for custom.
 */
export function setCustomerTabTip(
  tab: Tab,
  tip: number,
  accessToken: string | null,
  tipPercent: number | null = null,
) {
  return async () => {
    const { id } = tab;
    const rounded = Math.round(Math.max(0, Number(tip) || 0) * 100) / 100;
    const percent =
      tipPercent == null || !Number.isFinite(Number(tipPercent))
        ? null
        : Math.round(Number(tipPercent) * 100) / 100;

    try {
      const result = await invokeFunction<{ tip: number; tip_percent?: number | null; amount: number | null }>(
        'fastpass-payment/set-tip',
        {
          tabId: parseInt(String(id), 10),
          accessToken,
          tip: rounded,
          tipPercent: percent,
        },
      );

      dispatch(slice.actions.setCustomerTabTipSuccess({ tip: Number(result.tip) || rounded }));
      return result;
    } catch (error: any) {
      console.error('Error setting Fastpass tip:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export type PixCharge = {
  correlationID: string;
  status: string;
  value: number;
  brCode: string;
  qrCodeImage: string;
  expiresAt: string;
};

export function getPixCharge(tab: Tab, accessToken: string | null) {
  return async () => {
    dispatch(slice.actions.startLoading());

    const { id } = tab;

    try {
      // Same dedicated Fastpass edge function family as getPaymentSecret —
      // unauthenticated guest checkout, amount recomputed server-side.
      const result = await invokeFunction<PixCharge>('fastpass-payment/create-pix-charge', {
        tabId: parseInt(id),
        accessToken,
      });

      dispatch(slice.actions.getPixChargeSuccess(result));
      return result;
    } catch (error: any) {
      console.error('Error creating PIX charge:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export async function getPixChargeStatus(correlationID: string, tabId: string, accessToken: string | null) {
  return invokeFunction<{ correlationID: string; status: string; value: number; paidAt: string | null }>(
    'fastpass-payment/get-pix-status',
    { correlationID, tabId: parseInt(tabId, 10), accessToken },
  );
}

export async function cancelPixCharge(correlationID: string, tabId: string, accessToken: string | null) {
  return invokeFunction<{ cancelled: boolean }>('fastpass-payment/cancel-pix-charge', {
    correlationID,
    tabId: parseInt(tabId, 10),
    accessToken,
  });
}

export function sendFastpassConfirmationEmail(tabId: string, accessToken: string | null, email?: string) {
  return async () => {
    try {
      const result = await invokeFunction<{ sent: boolean; reason?: string }>(
        'fastpass-payment/send-confirmation-email',
        { tabId: parseInt(tabId, 10), accessToken, email },
      );
      dispatch(slice.actions.setEmailSentStatus(result.sent));
      return result;
    } catch (error: any) {
      console.error('sendFastpassConfirmationEmail error:', error);
      dispatch(slice.actions.setEmailSentStatus(false));
      throw error;
    }
  };
}

export async function getVenuePhotoSignedUrl(tabId: string, accessToken: string | null) {
  const result = await invokeFunction<{ signedUrl: string | null }>('get-venue-photo-url', {
    tabId: parseInt(tabId, 10),
    accessToken,
  });
  return result.signedUrl;
}

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

// Closes the tab via fastpass-payment/close-tab, which independently
// verifies the payment with Stripe/Woovi server-side before marking it
// paid (see migration 070 — update_tab_payment is no longer client-callable).
// paymentId is the Stripe PaymentIntent id or the Woovi PIX correlationID;
// pass null for a legitimate zero-total close (CheckoutZeroClose.tsx).
export function updateCustomerTab(
  customerTab: Tab,
  accessToken: string | null,
  paymentId: string | null,
  paymentGateway?: 'stripe' | 'woovi',
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await invokeFunction<{ tab?: any; closed?: boolean }>(
        'fastpass-payment/close-tab',
        {
          tabId: parseInt(customerTab.id),
          accessToken,
          paymentIntentId: paymentGateway === 'stripe' ? paymentId : undefined,
          correlationID: paymentGateway === 'woovi' ? paymentId : undefined,
        },
      );

      const transformedTab = result?.tab
        ? transformCustomerTabPayload(result.tab)
        : customerTab;

      dispatch(slice.actions.getUpdatedCustomerTabSuccess(transformedTab));

      return result;
    } catch (error: any) {
      console.error('Error updating customer tab:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateTab(data: {
  id: number;
  tabstatus: number;
  venueUserId?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Update tab status using RPC or Supabase update
      const result = await callRPC<{
        tab_id: number;
        tabstatus: number;
      }>('update_tab_status', {
        p_tab_id: data.id,
        p_tab_status: data.tabstatus,
        p_venue_user_id: data.venueUserId || null,
      });

      // Refresh via the same RPC as getTab so venue/orders/logs stay shaped correctly
      // (direct selectOne returns `venues`/`users` and was wiping `tab.venue`).
      const details = await callRPC<{ tab: Tab }>('get_venue_tab_details', {
        p_tab_id: data.id,
      });

      if (details?.tab) {
        dispatch(slice.actions.getTabSuccess(details.tab));
      }

      return result;
    } catch (error: any) {
      console.error('Error updating tab:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getCustomer(govId: string, venueId?: string | null) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await callRPC<{
        customer: any;
        open_tab?: {
          id?: number;
          venue_id?: string;
          venue_name?: string | null;
          tabstatus?: number;
          same_venue?: boolean;
        } | null;
      }>('get_user_by_govid', {
        p_govid: govId,
        p_venue_id: venueId || null,
      });

      if (!result?.customer) {
        dispatch(slice.actions.resetCustomer());
        return { customer: null, openTab: null };
      }

      const user = result.customer;

      const customer = {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        govId: user.govId,
        photo_url: user.photo_url,
        birthDate: user.birthDate,
        gender: user.gender,
      };

      const openRaw = result.open_tab ?? null;
      const openTab: CustomerOpenTab = openRaw
        ? {
            id: openRaw.id,
            venue_id: openRaw.venue_id,
            venue_name: openRaw.venue_name ?? null,
            tabstatus: openRaw.tabstatus,
            same_venue: !!openRaw.same_venue,
          }
        : null;

      dispatch(
        slice.actions.getCustomerSuccess({
          customer,
          openTab,
        }),
      );
      return { customer, openTab };
    } catch (error: any) {
      console.error('Error fetching customer:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function notifyClient(data: {
  tabId: number;
  userId: string;
  title?: string;
  body?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const tabId = data.tabId.toString();

      // Use Edge Function to send push notification
      await invokeFunction('push-notification', {
        userId: data.userId,
        title: data.title || 'Tab Update',
        body: data.body || 'Your tab has been updated',
        data: {
          tabId,
          type: 'tab_update',
          url: `tab_receipt/${tabId}`,
        },
      });

      dispatch(slice.actions.stopLoading());
    } catch (error: any) {
      console.error('Error notifying client:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function validateTab(data: {
  tab_id: number;
  tab_code: number;
  qr_code_token?: string; // Optional QR code token for enhanced validation
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get current venue_user_id from auth
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        throw new Error('User not authenticated or venue user not found');
      }

      // Use RPC function to validate tab
      const result = await callRPC('validate_tab', {
        p_tab_id: data.tab_id,
        p_tab_code: data.tab_code,
        p_venue_user_id: venueUserId,
        p_qr_code_token: data.qr_code_token || null,
      });
      
      dispatch(slice.actions.stopLoading());
      return result;
    } catch (error: any) {
      console.error('validation error', error);
      dispatch(slice.actions.hasError(error.message));
      throw new Error(error.message);
    }
  };
}

// ----------------------------------------------------------------------

export function createTabEntry(data: {
  customer_id: string;
  venue_id: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Get current venue_user_id from auth
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        throw new Error('User not authenticated or venue user not found');
      }

      // Use RPC function to create tab entry
      const result = await callRPC('create_tab_entry', {
        p_customer_id: data.customer_id,
        p_venue_id: data.venue_id,
        p_venue_user_id: venueUserId,
      });
      dispatch(slice.actions.stopLoading());
      dispatch(getTabs());
      return result;
    } catch (error: any) {
      console.error('Error creating tab entry:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function validateSplitEntry(data: {
  tab_id: number;
  customer_id: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const venueUserId = await getCurrentVenueUserId();
      if (!venueUserId) {
        throw new Error('User not authenticated or venue user not found');
      }

      const result = await callRPC('validate_split_entry', {
        p_tab_id: data.tab_id,
        p_customer_id: data.customer_id,
        p_venue_user_id: venueUserId,
      });

      dispatch(slice.actions.stopLoading());
      return result;
    } catch (error: any) {
      console.error('split entry validation error:', error);
      dispatch(slice.actions.hasError(error.message));
      throw new Error(error.message);
    }
  };
}

// ----------------------------------------------------------------------

export function bulkUpdateTabs(data: {
  tabIds: number[];
  tabstatus: number;
  venueUserId?: string;
}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // Update multiple tabs
      const updatePromises = data.tabIds.map((tabId) =>
        callRPC('update_tab_status', {
          p_tab_id: tabId,
          p_tab_status: data.tabstatus,
          p_venue_user_id: data.venueUserId || null,
        })
      );

      await Promise.all(updatePromises);

      dispatch(slice.actions.stopLoading());
      // Refresh tabs list
      dispatch(getTabs());
    } catch (error: any) {
      console.error('Error bulk updating tabs:', error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function getTabsStatistics() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // TODO: Implement statistics queries using Supabase
      // This is a complex query that aggregates data from multiple tables
      // For now, using a placeholder that can be enhanced later
      
      // Placeholder structure matching the expected format
      const statisticsData = {
        tabsSummary: {
          label: '',
          value: 0,
          count: 0,
          percent: 0,
          total: 0,
          average: 0,
          data: [],
        },
        publicSummary: {
          label: '',
          value: 0,
          count: 0,
          percent: 0,
          total: 0,
          average: 0,
          data: [],
        },
        tabMonthlyTotals: [],
        publicMonthlyTotals: [],
        totalsByProduct: [],
        totalsByStatus: [],
        totalsByType: [],
        totalsByVenue: [],
        totalsByTypePerWeekday: [],
      };

      // TODO: Implement actual queries using Supabase
      // For now, return placeholder structure
      dispatch(slice.actions.getTabsStatisticsSuccess(statisticsData));

    } catch (error: any) {
      dispatch(slice.actions.hasError(error));
      console.error('Error fetching statistics:', error);
    }
  };
}